import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import DashboardTab from './DashboardTab';
import NoData from '../../ui/noData/NoData';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import { useSelector } from 'react-redux';
import AiCheckoutTrackingStats from './stats/AiCheckoutTrackingStats';
import addDaysToDate from '../../lib/helper-functions/addDaysToDate';
import {
  DAYS_TO_ADD,
  defaultGraphTimeFilter,
} from '../../lib/constants/abstracts';
import { RootState } from '../../store/store';
import WebAuthnRegisterPrompt from '../webAuthn/WebAuthnRegisterPrompt';
import PageBlocker from '../../ui/cards/PageBlocker';
import { RoleGate, useEmployeeRole } from '../../lib/rbac';
import PendingProposalsCard from './PendingProposalsCard';
import MyProposalsCard from './MyProposalsCard';
import DashboardStatsStrip from './DashboardStatsStrip';
import GetAgencyLiveCard from './GetAgencyLiveCard';
import { isDemoMode } from '../../lib/demo/demo-mode';
import '../../styles/pages/dashboard/dashboardAgencyCards.scss';

// Public /demo only: example agents. Lazy, so it stays out of the main bundle.
const DemoAgentsTeaser = lazy(
  () => import('../demo/components/DemoAgentsTeaser')
);

declare const window: any;

type Props = {
  statistics: any;
};

const Dashboard = ({ statistics }: Props) => {
  const { t } = useTranslation();
  // const [statistics, setStatistics] = useState<any>({});
  const [isTimesUp, setIsTimesUp] = useState<boolean>(false);

  // Journey A — the first-run setup checklist is for the people who onboard the
  // ORG: owners and managers. Other roles (team_lead/analyst/read_only) join an
  // already-live agency, so the card never renders for them.
  const employeeRole = useEmployeeRole();
  const showOnboardingCard =
    employeeRole === 'owner' || employeeRole === 'manager';

  // const [isTrackable, setIsTrackable] = useState<boolean>(false);
  const [showTabs, setShowTabs] = useState<boolean>(false);
  const [timeFilter, setTimeFilter] = useState<string>(defaultGraphTimeFilter);
  const [customPeriodFilterDateRange, setCustomPeriodFilterDateRange] =
    useState<{
      start_date: string | undefined;
      end_date: string | undefined;
    }>({ start_date: undefined, end_date: undefined });
  const [eventFilter, setEventFilter] = useState<string>('purchase');
  const isAdminSession = useSelector(
    (state: RootState) => state.auth.authorization?.isAdminSession ?? false
  );
  const CMS_Type = useSelector(
    (state: RootState) => state.user.company?.cmsSelected ?? ''
  );
  const isTrackable = useSelector(
    (state: RootState) => state.user.stats?.is_trackable
  );
  const key = useSelector(
    (state: RootState) => state.user.stats?.ai_tracker_api_key
  );
  const isLoggedInViaWebAuthn =
    useSelector(
      (state: RootState) =>
        state.auth?.authorization?.webAuthn?.isLoggedInViaWebAuthn
    ) ?? false;
  const isWebAuthnRegistrationSkipped =
    useSelector(
      (state: RootState) =>
        state.auth?.authorization?.webAuthn?.isWebAuthnRegistrationSkipped
    ) ?? false;
  const isWebAuthnAvailableInAccount =
    useSelector(
      (state: RootState) =>
        state.auth?.authorization?.webAuthn?.isWebAuthnAvailableInAccount
    ) ?? false;
  const [
    shouldShowWebAuthnRegistrationPrompt,
    setShouldShowWebAuthnRegistrationPrompt,
  ] = useState<boolean>(false);
  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');

  useEffect(() => {
    setLoading('pending');
    if (statistics) {
      setLoading('success');
    } else {
      setLoading('null');
    }
  }, [statistics]);

  // Check if the tracking time is up
  useEffect(() => {
    if (statistics?.additional_stats?.tracker_configured_timestamp) {
      const dateTimeISOString =
        statistics?.additional_stats?.tracker_configured_timestamp;
      const futureDate = addDaysToDate(
        dateTimeISOString ? dateTimeISOString : '',
        DAYS_TO_ADD
      );

      const calculateTimeLeft = () => {
        const now = new Date().getTime();
        const targetTime = futureDate.getTime();
        const difference = targetTime - now;

        if (difference <= 0) {
          setIsTimesUp(true);
        }
      };

      calculateTimeLeft();
    }
  }, [statistics]);

  /* --------------------------------------------- */

  useEffect(() => {
    if (isTimesUp && key !== '') {
      setShowTabs(false);
    } else {
      setShowTabs(true);
    }
  }, [isTimesUp, key, statistics]);
  /* --------------------------------------------- */

  /* --------------- WebAuthn Registration Prompt show/hide logic - START --------------- */
  useEffect(() => {
    const shouldShowPrompt = () => {
      //* If WebAuthn is not supported, hide registration prompt
      if (!window.Webpass.isSupported()) {
        return false;
      }
      //* If user is already logged in via WebAuthn, hide registration prompt
      if (isLoggedInViaWebAuthn) {
        return false;
      }
      //* If user has skipped WebAuthn registration, hide registration prompt
      if (isWebAuthnRegistrationSkipped) {
        return false;
      }
      //* If user logged in as an Admin, hide registration prompt
      if (isAdminSession) {
        return false;
      }
      return true;
    };

    //* Commented below for the MCP Launch
    // setShouldShowWebAuthnRegistrationPrompt(shouldShowPrompt());
    //* Added below for the MCP Launch
    setShouldShowWebAuthnRegistrationPrompt(false);
  }, [
    isWebAuthnAvailableInAccount,
    isLoggedInViaWebAuthn,
    isWebAuthnRegistrationSkipped,
  ]);
  /* --------------- WebAuthn Registration Prompt show/hide logic - END --------------- */

  return (
    <>
      {loading === 'success' &&
        !statistics?.additional_stats?.tracker_configured_timestamp && (
          <PageBlocker />
        )}
      {!shouldShowWebAuthnRegistrationPrompt ? (
        <>
          <PageWrapperMain>
            <SubPageContentHolder>
              <SubPageInnerContentHolder
                title={t('DashboardOverview')}
                // #1 — removed the old checkout/event-tracking controls from the
                // dashboard header (the top-right "Select Event" / "Select Period"
                // filters). They governed the legacy checkout-tracking stats only,
                // not the agency cards below.
                // subTitle={t('DashboardSubTitle')}
              >
                {/* Agency portal cards — the real dashboard. Each card
                    self-fetches and self-gates via RoleGate, so they render
                    independently of the legacy checkout-tracking `statistics`
                    payload. (That payload is currently not fetched; the whole
                    body used to be gated on it via `loading === 'success'`,
                    which is why the dashboard rendered blank for everyone.) */}
                <div className='dashboardAgencyCards--container'>
                  {showOnboardingCard && <GetAgencyLiveCard />}
                  <RoleGate capability='proposals.view'>
                    <PendingProposalsCard />
                  </RoleGate>
                  {/* Journey B — analysts (and other proposers) track their
                      OWN submissions and their status here. Gated to roles
                      that can propose, so read-only never sees it. */}
                  <RoleGate capability='proposals.propose'>
                    <MyProposalsCard />
                  </RoleGate>
                  {/* The strip reads `GET /agency/stats`, which central-api
                      limits to owner/manager (StatsController role gate). Gate
                      on the matching FE capability (`stats.perUser`, same set
                      the /stats nav entry uses) instead of `stats.view`, which
                      every role holds — otherwise team_lead/analyst/read_only
                      got a permanent "Could not load stats." card from a 403. */}
                  <RoleGate capability='stats.perUser'>
                    <DashboardStatsStrip />
                  </RoleGate>
                </div>

                {isDemoMode() && (
                  <Suspense fallback={null}>
                    <DemoAgentsTeaser />
                  </Suspense>
                )}

                {/* Legacy checkout-tracking stats — only render when the
                    seller stats payload is actually present. */}
                {loading === 'pending' && <LoadingSpinner />}
                {loading === 'error' && (
                  <NoData
                    title={t('ErrorFetchingData')}
                    subTitle={t('SomethingWentWrong-msg')}
                  />
                )}
                {loading === 'success' && (
                  <>
                    {showTabs && !isTimesUp && (
                      <DashboardTab stats={statistics} isTimesUp={isTimesUp} />
                    )}
                    <AiCheckoutTrackingStats
                      isTrackable={isTrackable}
                      isTimesUp={isTimesUp}
                      stats={statistics}
                      timeFilter={timeFilter}
                      customPeriodFilterDateRange={customPeriodFilterDateRange}
                      eventFilter={eventFilter}
                    />
                  </>
                )}
              </SubPageInnerContentHolder>
            </SubPageContentHolder>
          </PageWrapperMain>
        </>
      ) : (
        <WebAuthnRegisterPrompt />
      )}
    </>
  );
};

export default Dashboard;
